/**
 * "View as agent" — where the admin portal's new tab lands.
 *
 * /view-as#code=…   trades the one-time code for a read-only session of the
 *                   agent's CIDAS (kept in this tab only, see api/client.js),
 *                   then reloads into the app as that agent.
 * /view-as/ended    what the tab shows once the session is over — and, in a tab
 *                   that held a view, on every other route too (App.jsx), so
 *                   the view's tab never turns into this browser's own sign-in.
 *
 * The code rides in the fragment so it never reaches a server log, and it is
 * wiped from the address bar before anything else happens.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { BASE, viewAs, VIEW_ENDED_PATH } from '../api/client';

// A code works once. React may run the effect twice (StrictMode in development),
// so the exchange is remembered per code rather than fired again.
const exchanges = new Map();
// A bare request, not the shared client: that one attaches whatever sign-in this
// browser already holds and, on a 401, would "refresh" it — writing another
// agent's profile into the view.
const exchange = (code) => {
  if (!exchanges.has(code)) {
    exchanges.set(code, axios.post(`${BASE}/api/agent/auth/view-as`, { code }).then((r) => r.data?.data ?? r.data));
  }
  return exchanges.get(code);
};

export default function ViewAsPage({ ended: endedTab = false }) {
  const { pathname } = useLocation();
  const ended = endedTab || pathname === VIEW_ENDED_PATH;
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ended) {
      viewAs.clear();
      // Any route of an ended view's tab lands here; say so in the address bar.
      if (window.location.pathname !== VIEW_ENDED_PATH) window.history.replaceState(null, '', VIEW_ENDED_PATH);
      return;
    }
    const code = new URLSearchParams(window.location.hash.slice(1)).get('code') || exchanges.keys().next().value;
    window.history.replaceState(null, '', '/view-as');
    // From here this is a view tab, whether or not the code works: a failed
    // exchange must not fall through to this browser's own sign-in either.
    viewAs.markTab();
    if (!code) { setError('This link has no code. Open the agent again from the admin portal.'); return; }
    viewAs.clear();
    exchange(code)
      .then((d) => {
        viewAs.set({ accessToken: d.accessToken, agent: d.agent, session: d.session });
        // A full load, so every cache and context starts from this agent.
        window.location.replace('/');
      })
      .catch((e) => setError(e.response?.data?.error?.message || e.message || 'Could not open this agent’s CIDAS.'));
  }, [ended]);

  const title = ended ? 'The view has ended' : error ? 'Couldn’t open CIDAS' : 'Opening the agent’s CIDAS…';
  const body = ended
    ? 'You were viewing an agent’s CIDAS from the admin portal. To look again, open the agent from the portal.'
    : error || 'Read only, for 30 minutes.';

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg)' }}>
      <div style={{ maxWidth: 380, textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.55, marginTop: 8 }}>{body}</div>
        {(ended || error) && (
          <button
            type="button"
            onClick={() => window.close()}
            style={{ marginTop: 18, height: 40, padding: '0 18px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontWeight: 600, cursor: 'pointer' }}
          >
            Close this tab
          </button>
        )}
      </div>
    </div>
  );
}
